"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.historyQuerySchema = exports.historyListSchema = exports.historyEntrySchema = exports.historyChangeSchema = exports.historyValueSchema = exports.HISTORY_CHANGE_KINDS = exports.HISTORY_ENTITY_TYPES = void 0;
const zod_1 = require("zod");
const pagination_1 = require("../pagination");
const list_filter_1 = require("../list-filter");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · ONE shape for every history: the audit log and each entity's. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` U-8 / U-9. Before this there were three wire shapes over the same `audit_log`
 * table (`taskHistoryEntrySchema`, `contractHistoryEntrySchema`, `personHistoryEntrySchema`), none
 * of them paged, and every screen turned raw keys (`status_id`) and raw ids into prose on its own.
 *
 * ⚠️⚠️ **THE SERVER RESOLVES, THE SCREEN LABELS.** A row leaves the API with its ids already turned
 * into names (`status_id` → *Folyamatban*, a member id → a person) and its keys normalised to one
 * camelCase `field`; the screen only translates that `field` and the `action`. Ids are resolved in
 * the database, where the names are — and words stay in the web catalogue (rule 7).
 *
 * ⚠️ **A deleted record is still named**: its label comes from the row's own `before`, so the log
 * of a deletion does not say "unknown".
 */
/** What a row is about — the screen's entity column, and the audit page's entity filter. */
exports.HISTORY_ENTITY_TYPES = [
    'task',
    'comment',
    'worklog',
    'project',
    'project_member',
    'allocation',
    'client',
    'contract',
    'line_item',
    'period',
    'invoice',
    'external_cost',
    /* 20 Oct 2026 · Phase 6. */
    'deal',
    'contact',
    'person',
    'leave_request',
    'overtime_request',
    'file',
    'report',
    'metafield',
    'role',
    'integration',
    'settings',
    'workspace',
    'other',
];
/**
 * The four kinds the owner asked to see a change as: *"how was it changed (create, delete,
 * modify)"*. `other` is everything that is neither — a comment, an approval, a sync run.
 */
exports.HISTORY_CHANGE_KINDS = ['create', 'update', 'delete', 'other'];
/**
 * One side of a change, already readable. `kind` tells the screen how to FORMAT `text` (a date in
 * the reader's locale, hours as `H:MM`, money with its currency, rich text rendered) — the value
 * itself is final, so a screen never resolves an id.
 *
 * ⚠️ The `text` per kind: `money` is `"<plain decimal> <ISO code>"` (`"1200.50 HUF"` — dot decimal,
 * no separators; the amount alone when the row has no currency); `hours` a plain decimal (`"7.5"`);
 * `date` `YYYY-MM-DD`; `datetime` ISO 8601; `bool` `"true"` / `"false"`; `list` comma-separated.
 */
exports.historyValueSchema = zod_1.z.object({
    kind: zod_1.z.enum(['text', 'date', 'datetime', 'hours', 'money', 'bool', 'html', 'list']),
    text: zod_1.z.string(),
});
exports.historyChangeSchema = zod_1.z.object({
    /**
     * camelCase, whatever the audit row stored (`status_id` and `statusId` both arrive as
     * `statusId`). The screen labels it from `history.field.<field>`, falling back to the key.
     */
    field: zod_1.z.string(),
    before: exports.historyValueSchema.nullable(),
    after: exports.historyValueSchema.nullable(),
});
exports.historyEntrySchema = zod_1.z.object({
    id: zod_1.z.string(),
    at: zod_1.z.string(),
    /** The audit action, `task.update` — the screen words it from `history.action.*`. */
    action: zod_1.z.string(),
    kind: zod_1.z.enum(exports.HISTORY_CHANGE_KINDS),
    actor: zod_1.z.object({
        memberId: zod_1.z.string().nullable(),
        /** Null for the system — a sync, a scheduled job, the demo seed. */
        name: zod_1.z.string().nullable(),
        avatarSeed: zod_1.z.string().nullable(),
    }),
    /** How the change arrived: the app, the API, the in-app agent, an MCP client. */
    channel: zod_1.z.enum(['ui', 'api', 'agent', 'mcp']).nullable(),
    entity: zod_1.z.object({
        type: zod_1.z.enum(exports.HISTORY_ENTITY_TYPES),
        id: zod_1.z.string().nullable(),
        /** The public identifier (R-51) or a task's key — what the screen shows and links by. */
        identifier: zod_1.z.string().nullable(),
        /** The record's name or title, from the live row or, for a deletion, from `before`. */
        label: zod_1.z.string().nullable(),
        /** The workspace-relative address to open it at, when it still exists and has one. */
        path: zod_1.z.string().nullable(),
    }),
    /** Field by field. Empty for an action that changed no field (a sync, an export). */
    changes: zod_1.z.array(exports.historyChangeSchema),
    /**
     * A comment's text, an approval's note — the one thing a row says that is not a field change.
     * U-8 records comment bodies from 14 Oct 2026; older rows carry none.
     */
    note: exports.historyValueSchema.nullable(),
});
exports.historyListSchema = (0, pagination_1.listResponseSchema)(exports.historyEntrySchema);
/**
 * `GET /audit` and every `GET /<entity>/:id/history`. Offset-paged like every list (`page.ts`),
 * newest first. ⚠️ `q` matches the actor's name, the entity's identifier and label, and the
 * action — never the jsonb payload, which would be a scan of the whole log.
 */
exports.historyQuerySchema = zod_1.z.object({
    q: zod_1.z.string().max(200).optional(),
    /* 3 Nov 2026 · comma lists — OR within, AND across; a bad item is dropped (`list-filter.ts`). */
    actorMemberId: (0, list_filter_1.commaListSchema)(zod_1.z.uuid()),
    entityType: (0, list_filter_1.commaListSchema)(zod_1.z.enum(exports.HISTORY_ENTITY_TYPES)),
    kind: (0, list_filter_1.commaListSchema)(zod_1.z.enum(exports.HISTORY_CHANGE_KINDS)),
    /** Calendar dates, inclusive, in the reader's timezone. */
    from: zod_1.z.string().optional(),
    to: zod_1.z.string().optional(),
    limit: zod_1.z.coerce.number().int().optional(),
    offset: zod_1.z.coerce.number().int().optional(),
});
//# sourceMappingURL=audit.js.map